/// <amd-module name="Core/Medius.Core.Web/Scripts/components/emailStatusTab/components/emailEntry"/>

import { Button, FlexLayout, Text } from '@medius/ui-controls';
import { translate } from 'Core/Medius.Core.Web/Scripts/lib/globalization';
import * as React from 'react';
import { EmailEntryDto, EmailType, parseEmailType } from '../models/emailEntryDto';
import { EmailStatusRenderer, EmailStatusRendererRecipient } from '../common/emailStatusRenderer';
import { DatePresenter } from 'Core/Medius.Core.Web/Scripts/shared/components/datePresenter';
import { getEmailMessage } from '../api/emailMessageApi';
import { ResendEmail, ResendEmailInfo } from '../../sendEmailCapture/sendEmailContainerService';
import { SendEmailDialog } from '../../sendEmailDialog/sendEmailDialog';
import { EmailMessageDto } from '../models/emailMessageDto';
import { Contact, SendEmailDialogFormFields } from 'Core/Medius.Core.Web/Scripts/components/sendEmailDialog/types';
import { error } from 'Core/Medius.Core.Web/Scripts/Medius/core/notification';

export const captureDocumentTypeName = "Medius.PurchaseToPay.CCF.Entities.CaptureDocument";

type EmailInfoMetadata = { docId: number; docType: string; hash: string; tag: string, userTaskId?: number };
type CaptureEmailInfoMetadata = { docId: number; docType: string; ccfInvoiceId?: string; userTaskId?: number };

type EmailMetadata = EmailInfoMetadata | CaptureEmailInfoMetadata;

type EmailEntryProps = {
    emailEntry: EmailEntryDto;
    emailMetadata: EmailMetadata;
    addressBook: Contact[];
};

function isCaptureInvoiceMetadata(
    meta: EmailMetadata
): meta is CaptureEmailInfoMetadata {
    return meta.docType === captureDocumentTypeName;
}

export const EmailEntry = ({ emailEntry, emailMetadata, addressBook }: EmailEntryProps): React.ReactElement => {
    const date= new Date(emailEntry.timestamp);
    const [emailMessage, setEmailMessage] = React.useState<EmailMessageDto>();
    const [isResendEmailModalOpen, setIsResendEmailModalOpen] = React.useState<boolean>(false);

    const rendererRecipients: EmailStatusRendererRecipient[] = React.useMemo(() => emailEntry.recipients.map((recipient) => ({
        emailAddress: recipient.emailAddress,
        type: recipient.type,
        status: recipient.emailStatus,
        latestEventReason: recipient.latestEventReason,
    })), [emailEntry.recipients]);

    const toRecipients = React.useMemo(() => rendererRecipients.filter(r => r.type === 'To').map(r => ({ displayName: r.emailAddress, emailAddress: r.emailAddress, deliveryFailed: (r.status === 'red' || r.status === 'hollowRed'), errorMessage: r.latestEventReason })), [rendererRecipients]);
    const ccRecipients = React.useMemo(() => rendererRecipients.filter(r => r.type === 'Cc').map(r => ({ displayName: r.emailAddress, emailAddress: r.emailAddress, deliveryFailed: (r.status === 'red' || r.status === 'hollowRed'), errorMessage: r.latestEventReason })), [rendererRecipients]);
    const handleEditAndResendClick = React.useCallback(async () => {
        try {
            const message = await getEmailMessage(emailEntry.id);
            if (message) {
                setEmailMessage(message);
                setIsResendEmailModalOpen(true);
            }
        } catch {
            error(translate("#Core/failedToLoadEmailMessage"));
        }
    }, [emailEntry.id]);

    const handleSendAction = React.useCallback(async (email: SendEmailDialogFormFields) => {
        try {
            const resendForCapture = isCaptureInvoiceMetadata(emailMetadata);
            const asHtml = true;
            if (resendForCapture) {
                const captureEmailInfo = new ResendEmailInfo(
                    email.subject,
                    email.body,
                    emailEntry.emailType,
                    emailMetadata.docId,
                    emailMetadata.docType,
                    undefined,
                    undefined,
                    emailMetadata.ccfInvoiceId,
                    emailMetadata.userTaskId);

                await ResendEmail(
                    captureEmailInfo,
                    email.recipients.map(r => r.emailAddress),
                    email.ccs.map(r => r.emailAddress),
                    email.sendMeACopy,
                    asHtml,
                    resendForCapture
                );
            } else {
                const emailInfo = new ResendEmailInfo(
                    email.subject,
                    email.body,
                    emailEntry.emailType,
                    emailMetadata.docId,
                    emailMetadata.docType,
                    emailMetadata.hash,
                    emailMetadata.tag,
                );
                emailInfo.userTaskId = emailMetadata.userTaskId;
                await ResendEmail(
                    emailInfo,
                    email.recipients.map(r => r.emailAddress),
                    email.ccs.map(r => r.emailAddress),
                    email.sendMeACopy,
                    asHtml,
                    resendForCapture
                );
            }
        }
        catch {
            error(translate("#Core/emailSentUnsuccessfully"));
        }
        finally {
            setIsResendEmailModalOpen(false);
        }

    }, [emailMetadata]);

    const getEmailTitle = (): string => {
        switch (parseEmailType(emailEntry.emailType)) {
            case EmailType.SendInvoiceRejectionToSupplier:
                return translate('#Core/invoiceRejectionEmailSent');
            case EmailType.SendToSupplier:
                return translate('#Core/purchaseOrderEmailSentToSupplier');
            case EmailType.SendInvoiceImage:
                return translate('#Core/emailWithInvoiceImage');
            case EmailType.SendCreditMemoToSupplier:
                return translate('#Core/emailWithShortPayment');
            case EmailType.SendAdjustmentRequestToSupplier:
                return translate('#Core/emailWithAdjustmentRequest');
            default:
                return translate('#Core/emailSent');
        }
    };

    return (
        <FlexLayout
            direction="column"
            itemRowSpacing="small"
            className="email-entry"
        >
            <FlexLayout
                direction="row"
                spacerMode="on"
                itemColumnSpacing="small"
                verticalAlignment="center"
                style={{ justifyContent: 'space-between' }}
            >
                <Text variant="heading3" className="email-entry__title" data-testid="email-title">
                    {getEmailTitle()}
                </Text>
                <Button
                    label={translate('#Core/editAndResend')}
                    onClick={handleEditAndResendClick}
                    variant="inline"
                    data-testid="edit-resend-button"
                />
            </FlexLayout>

            <FlexLayout itemColumnSpacing="small">
                <Text variant="body" id="email-entry__sender" data-testid="sender-name">
                    {emailEntry.senderName}
                </Text>
                <Text variant="body" id="email-entry__timestamp" data-testid="timestamp">
                    • <DatePresenter
                        date={date}
                        format="g"
                    />
                </Text>
            </FlexLayout>

            <EmailStatusRenderer
                recipients={rendererRecipients}
                layout="flex"
                labels={{
                    to: translate('#Core/emailTo'),
                    cc: translate('#Core/emailCc'),
                    bcc: translate('#Core/emailBcc'),
                }}
            />
            {
                emailMessage ?
                    <SendEmailDialog
                        title={translate('#Core/editAndResendEmailTitle')}
                        sendMeACopy={false}
                        subject={emailMessage.subject}
                        recipients={toRecipients}
                        body={emailMessage.body}
                        isOpen={isResendEmailModalOpen}
                        addressBook={addressBook}
                        onSendAction={handleSendAction}
                        ccs={ccRecipients}
                        onRequestClose={() => setIsResendEmailModalOpen(false)}
                    />
                    : <></>
            }
        </FlexLayout>
    );
};
